<template>
  <label class="custom-checkbox" :class="{ disabled: disabled }">
    <input
      type="checkbox"
      :checked="modelValue"
      :disabled="disabled"
      class="custom-checkbox__input"
      @change="handleChange"
    />
    <span class="custom-checkbox__checkmark">
      <svg
        v-if="modelValue"
        class="custom-checkbox__icon"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M13.5 4.5L6 12L2.5 8.5"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
    <div class="custom-checkbox__label-container">
      <span v-if="label" class="custom-checkbox__label">{{ label }}</span>
      <span v-if="sublabel" class="custom-checkbox__sublabel">{{
        sublabel
      }}</span>
    </div>
  </label>
</template>

<script setup>
import { defineProps, defineEmits } from "vue";

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "",
  },
  sublabel: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "change"]);

const handleChange = (event) => {
  const checked = event.target.checked;
  emit("update:modelValue", checked);
  emit("change", checked);
};
</script>

<style scoped>
.custom-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--primary-blue);
  letter-spacing: 0.08px;
}

.custom-checkbox.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.custom-checkbox__input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.custom-checkbox__checkmark {
  position: relative;
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.custom-checkbox__input:checked + .custom-checkbox__checkmark {
  background-color: var(--primary-blue);
  border-color: var(--primary-blue);
}

.custom-checkbox__input:checked
  + .custom-checkbox__checkmark
  .custom-checkbox__icon {
  color: white;
}

.custom-checkbox__input:focus + .custom-checkbox__checkmark {
  outline: none;
}

.custom-checkbox__input:disabled + .custom-checkbox__checkmark {
  background-color: #f5f5f5;
  border-color: #d1d5db;
  cursor: not-allowed;
}

.custom-checkbox__input:disabled:checked + .custom-checkbox__checkmark {
  background-color: #9ca3af;
  border-color: #9ca3af;
}

.custom-checkbox__icon {
  color: white;
  transition: color 0.2s ease;
}

.custom-checkbox__label {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--primary-blue);
  letter-spacing: 0.08px;
}

.custom-checkbox.disabled .custom-checkbox__label {
  color: #9ca3af;
}

.custom-checkbox__label-container {
  display: flex;
  flex-direction: column;
}

.custom-checkbox__sublabel {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4%;
  color: var(--neutral-700);
}
/* Focus styles for keyboard navigation */
.custom-checkbox:focus-within .custom-checkbox__checkmark {
  outline: none;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .custom-checkbox__checkmark {
    border-width: 3px;
  }

  .custom-checkbox:hover:not(.disabled) .custom-checkbox__checkmark {
    border-width: 3px;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .custom-checkbox__checkmark {
    transition: none;
  }

  .custom-checkbox__icon {
    transition: none;
  }
}
</style>
